import React from 'react';

import { standardRequest } from 'shared/utils/request';
import usePrevious from 'shared/utils/usePrevious';
import { Document, Person, Row } from 'talent_matching/types';
import {
  getDefaultSidePanelTab,
  isSidePanelTabVisible,
} from './side_panel_tab_helpers';

// We need a stable "resume identity" to decide when to auto-open the extended preview.
// The resume doc can change without changing `applicationId` (e.g. async resume load, cache refresh
// after calibration updates). Using an id when available, falling back to a URL, lets us detect
// "new resume content" vs. "user manually closed the preview".
const getResumeDocumentSyncKey = (
  document: Document | null | undefined
): string | null => {
  if (!document) {
    return null;
  }

  if (typeof document.id === 'number') {
    return `id:${document.id}`;
  }

  return `url:${document.downloadUrl}`;
};

// Read this as a required gate plus a list of reasons to auto-open.
// Gate: only open the resume preview in wide layouts, when the candidate has a
// resume, no resume preview is already open, and LinkedIn is not occupying the
// extended preview area. LinkedIn is its own preview mode; if it is active for
// the current candidate, resume auto-open should not replace it. On candidate
// switch we clear stale LinkedIn first and pass `effectiveLinkedinPreview`, so
// the next candidate can still auto-open their resume normally.
// Reasons: first panel open, candidate switch unless dismissed, an already-open
// preview following the candidate switch, or the same candidate's resume loading
// or changing after the panel is already visible.
const shouldAutoOpenExtendedResumePreview = ({
  allowExtendedPreview,
  resumeDocument,
  extendedResumePreview,
  linkedinPreview,
  isFirstVisibleApplication,
  isNewApplication,
  hadOpenPreview,
  isSameApplication,
  resumeDocumentChanged,
  isResumePreviewDismissed,
}: {
  allowExtendedPreview: boolean;
  resumeDocument: Document | null;
  extendedResumePreview: Document | null;
  linkedinPreview: string | null;
  isFirstVisibleApplication: boolean;
  isNewApplication: boolean;
  hadOpenPreview: boolean;
  isSameApplication: boolean;
  resumeDocumentChanged: boolean;
  isResumePreviewDismissed: boolean;
}) =>
  allowExtendedPreview &&
  resumeDocument !== null &&
  !extendedResumePreview &&
  !linkedinPreview &&
  (isFirstVisibleApplication ||
    (isNewApplication && !isResumePreviewDismissed) ||
    hadOpenPreview ||
    (isSameApplication && resumeDocumentChanged));

export const usePreventScroll = (ref: React.RefObject<HTMLDivElement>) => {
  React.useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const element = ref.current;
      if (element) {
        const isScrollable = element.scrollHeight > element.clientHeight;
        if (!isScrollable) {
          event.preventDefault();
        }
      }
    };

    const element = ref.current;
    element?.addEventListener('wheel', handleWheel);

    return () => {
      element?.removeEventListener('wheel', handleWheel);
    };
  }, [ref]);
};

export const useViewportWidth = (fallbackWidth: number) => {
  const [viewportWidth, setViewportWidth] = React.useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : fallbackWidth
  );

  React.useEffect(() => {
    const updateWidth = () => setViewportWidth(window.innerWidth);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return viewportWidth;
};

export const useApplicationViewTracking = ({
  isLoading,
  applicationId,
  appReviewSessionId,
}: {
  isLoading: boolean;
  applicationId: number | null | undefined;
  appReviewSessionId: number | null | undefined;
}) => {
  React.useEffect(() => {
    if (!isLoading && applicationId && appReviewSessionId) {
      standardRequest(
        `/applications/review/app_review/${appReviewSessionId}/record_view/${applicationId}`,
        {
          method: 'POST',
        }
      );
    }
  }, [isLoading, applicationId, appReviewSessionId]);
};

export const useSyncResumePreviewOnCandidateChange = ({
  applicationId,
  applicationResumeDocument,
  extendedResumePreview,
  linkedinPreview,
  modalDocument,
  setExtendedResumePreview,
  setLinkedinPreview,
  setModalDocument,
  allowExtendedPreview,
  isVisible,
  isResumePreviewDismissed,
}: {
  applicationId: number | null | undefined;
  applicationResumeDocument: Document | null | undefined;
  extendedResumePreview: Document | null;
  linkedinPreview: string | null;
  modalDocument: Document | null;
  setExtendedResumePreview: (document: Document | null) => void;
  setLinkedinPreview: (path: string | null) => void;
  setModalDocument: (document: Document | null) => void;
  allowExtendedPreview: boolean;
  isVisible: boolean;
  isResumePreviewDismissed: boolean;
}) => {
  const previousApplicationIdRef = React.useRef<number | null>(null);
  const previousResumeDocumentKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isVisible) {
      // The side panel component stays mounted when it's hidden (toggling the same row),
      // so reset our "previous" refs on close. This makes reopening the same candidate
      // behave like a fresh open and re-triggers the auto-open logic.
      previousApplicationIdRef.current = null;
      previousResumeDocumentKeyRef.current = null;
      return;
    }

    const currentApplicationId = applicationId ?? null;
    const previousApplicationId = previousApplicationIdRef.current;
    const isNewApplication = currentApplicationId !== previousApplicationId;
    const isFirstVisibleApplication = previousApplicationId === null;
    const isSameApplication = currentApplicationId === previousApplicationId;
    const resumeDocumentKey = getResumeDocumentSyncKey(
      applicationResumeDocument
    );
    const resumeDocumentChanged =
      resumeDocumentKey !== previousResumeDocumentKeyRef.current;
    const resumeDocument = applicationResumeDocument ?? null;
    const shouldResetLinkedinPreview =
      isNewApplication && Boolean(linkedinPreview);
    const effectiveLinkedinPreview = shouldResetLinkedinPreview
      ? null
      : linkedinPreview;
    const shouldCloseResumePreview = Boolean(effectiveLinkedinPreview);
    const hadOpenPreview = Boolean(
      extendedResumePreview || modalDocument || linkedinPreview
    );

    if (shouldResetLinkedinPreview) {
      setLinkedinPreview(null);
    }

    if (shouldCloseResumePreview) {
      setExtendedResumePreview(null);
      setModalDocument(null);
    }

    if (isNewApplication && !shouldCloseResumePreview) {
      if (extendedResumePreview) {
        setExtendedResumePreview(resumeDocument);
      }
      if (modalDocument) {
        setModalDocument(resumeDocument);
      }
    }
    if (
      shouldAutoOpenExtendedResumePreview({
        allowExtendedPreview,
        resumeDocument,
        extendedResumePreview,
        linkedinPreview: effectiveLinkedinPreview,
        isFirstVisibleApplication,
        isNewApplication,
        hadOpenPreview,
        isSameApplication,
        resumeDocumentChanged,
        isResumePreviewDismissed,
      })
    ) {
      setExtendedResumePreview(resumeDocument);
    }

    previousApplicationIdRef.current = currentApplicationId;
    previousResumeDocumentKeyRef.current = resumeDocumentKey;
  }, [
    applicationId,
    applicationResumeDocument,
    extendedResumePreview,
    linkedinPreview,
    modalDocument,
    setExtendedResumePreview,
    setLinkedinPreview,
    setModalDocument,
    allowExtendedPreview,
    isVisible,
    isResumePreviewDismissed,
  ]);
};

// Sets the default side panel tab when no prior tab selection is stored, applying the
// selection only after the panel opens or the candidate changes and the details finish loading.
export const useDefaultSidePanelTab = ({
  applicationId,
  application,
  isLoading,
  isPanelVisible,
  activeSidePanelTab,
  setActiveSidePanelTab,
}: {
  applicationId: number | null | undefined;
  application: Person | null;
  isLoading: boolean;
  isPanelVisible?: boolean;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
}) => {
  const hasStoredSelection = Boolean(activeSidePanelTab);
  const hasVisibleStoredSelection = activeSidePanelTab
    ? isSidePanelTabVisible(application, activeSidePanelTab)
    : false;
  const previousApplicationId = usePrevious(applicationId);
  const previousIsPanelVisible = usePrevious(isPanelVisible);
  const pendingDefaultTab = React.useRef(
    !hasStoredSelection || !hasVisibleStoredSelection
  );

  React.useEffect(() => {
    const isVisible = Boolean(isPanelVisible);
    const wasVisible = Boolean(previousIsPanelVisible);
    const isOpening = isVisible && !wasVisible;
    const isNewApplication = applicationId !== previousApplicationId;

    if (hasStoredSelection && hasVisibleStoredSelection) {
      pendingDefaultTab.current = false;
      return;
    }

    if (isOpening || isNewApplication) {
      pendingDefaultTab.current = true;
    }

    if (pendingDefaultTab.current && isVisible && !isLoading && applicationId) {
      setActiveSidePanelTab(getDefaultSidePanelTab(application));
      pendingDefaultTab.current = false;
    }
  }, [
    application,
    applicationId,
    isLoading,
    isPanelVisible,
    activeSidePanelTab,
    setActiveSidePanelTab,
    hasStoredSelection,
    hasVisibleStoredSelection,
    previousApplicationId,
    previousIsPanelVisible,
  ]);
};

export const useEscapeKeyToClose = ({
  modalsOpen,
  extendedResumePreview,
  modalDocument,
  isVisible,
  handleClosePanel,
  dismissExtendedResumePreview,
  setModalDocument,
}: {
  modalsOpen: boolean;
  extendedResumePreview: Document | null;
  modalDocument: Document | null;
  isVisible?: boolean;
  handleClosePanel: () => void;
  dismissExtendedResumePreview: () => void;
  setModalDocument: (document: Document | null) => void;
}) => {
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      const stopEscape = () => {
        event.stopPropagation();
        event.preventDefault();
      };

      if (modalsOpen) {
        return;
      }

      if (extendedResumePreview) {
        dismissExtendedResumePreview();
        stopEscape();
        return;
      }
      if (modalDocument) {
        setModalDocument(null);
        stopEscape();
        return;
      }

      if (isVisible) {
        handleClosePanel();
        stopEscape();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [
    dismissExtendedResumePreview,
    extendedResumePreview,
    handleClosePanel,
    isVisible,
    modalDocument,
    modalsOpen,
    setModalDocument,
  ]);
};

// When the panel opens, move focus into it so keyboard users land in the panel
// (ARIA grid pattern: Enter opens → focus enters the panel). Programmatic focus
// won't show a focus ring for mouse-initiated opens. Returning focus to the
// originating table row on close is handled in useSidePanel.
export const useFocusPanelOnOpen = ({
  isVisible,
  panelContentRef,
}: {
  isVisible?: boolean;
  panelContentRef: React.RefObject<HTMLElement>;
}) => {
  const wasVisibleRef = React.useRef(false);

  React.useEffect(() => {
    if (isVisible && !wasVisibleRef.current) {
      panelContentRef.current?.focus();
    }
    wasVisibleRef.current = isVisible ?? false;
  }, [isVisible, panelContentRef]);
};

// Don't hijack the shortcut while the user is typing (e.g. notes), where
// Alt+Arrow may mean word navigation.
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const { tagName } = target;
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    target.isContentEditable
  );
};

// Power-user shortcut: Alt+Left / Alt+Right move to the previous/next candidate
// from anywhere in the panel. A modifier key is required (WCAG 2.1.4) so we never
// intercept bare arrows, which assistive tech uses to read content. While the
// panel is open we own Alt+Arrow (preventing browser back/forward), but defer to
// modals and text editing.
export const useAltArrowNavigation = ({
  isVisible,
  modalsOpen,
  modalDocument,
  previousCandidate,
  nextCandidate,
  selectCandidate,
}: {
  isVisible?: boolean;
  modalsOpen: boolean;
  modalDocument: Document | null;
  previousCandidate?: Row | null;
  nextCandidate?: Row | null;
  selectCandidate?: (row: Row) => void;
}) => {
  React.useEffect(() => {
    if (!isVisible || !selectCandidate) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey) {
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }
      if (modalsOpen || modalDocument || isEditableTarget(event.target)) {
        return;
      }

      // Own the shortcut while the panel is open: consume it even at the
      // first/last candidate so it never falls through to browser back/forward.
      event.preventDefault();
      event.stopPropagation();

      const candidate =
        event.key === 'ArrowLeft' ? previousCandidate : nextCandidate;
      if (candidate) {
        selectCandidate(candidate);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [
    isVisible,
    modalsOpen,
    modalDocument,
    previousCandidate,
    nextCandidate,
    selectCandidate,
  ]);
};

export const useExtendedPreviewVsModal = ({
  allowExtendedPreview,
  extendedResumePreview,
  modalDocument,
  setExtendedResumePreview,
  setModalDocument,
}: {
  allowExtendedPreview: boolean;
  extendedResumePreview: Document | null;
  modalDocument: Document | null;
  setExtendedResumePreview: (document: Document | null) => void;
  setModalDocument: (document: Document | null) => void;
}) => {
  React.useEffect(() => {
    if (!allowExtendedPreview && extendedResumePreview) {
      setModalDocument(extendedResumePreview);
      setExtendedResumePreview(null);
    } else if (
      allowExtendedPreview &&
      modalDocument &&
      !extendedResumePreview
    ) {
      setExtendedResumePreview(modalDocument);
      setModalDocument(null);
    }
  }, [
    allowExtendedPreview,
    extendedResumePreview,
    modalDocument,
    setExtendedResumePreview,
    setModalDocument,
  ]);
};
